import React, { createContext, useContext, useState, ReactNode } from 'react';
import type { ImportProgress } from '../services/csvImportService';

interface CsvImportContextState {
  isImporting: boolean;
  progress: ImportProgress | null;
  isMinimized: boolean;
  startImport: () => void;
  updateProgress: (progress: ImportProgress) => void;
  completeImport: () => void;
  minimizeModal: () => void;
  restoreModal: () => void;
  cancelImport: () => void;
  setOnImportComplete: (callback: () => void) => void;
}

const CsvImportContext = createContext<CsvImportContextState | undefined>(undefined);

export const CsvImportProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isImporting, setIsImporting] = useState(false);
  const [progress, setProgress] = useState<ImportProgress | null>(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const [onImportCompleteCallback, setOnImportCompleteCallback] = useState<(() => void) | null>(null);

  const startImport = () => {
    setIsImporting(true);
    setIsMinimized(false);
    setProgress({
      completed: 0,
      total: 0,
      successful: [],
      failed: []
    });
  };

  const updateProgress = (newProgress: ImportProgress) => {
    setProgress(newProgress);
  };

  const completeImport = () => {
    setIsImporting(false);
    setIsMinimized(false);
    
    // Call the refresh callback if set
    if (onImportCompleteCallback) {
      onImportCompleteCallback();
    }
  };

  const minimizeModal = () => {
    setIsMinimized(true);
  };

  const restoreModal = () => {
    setIsMinimized(false);
  };

  const cancelImport = () => {
    setIsImporting(false);
    setIsMinimized(false);
    setProgress(null);
  };

  const setOnImportComplete = (callback: () => void) => {
    setOnImportCompleteCallback(() => callback);
  };

  return (
    <CsvImportContext.Provider
      value={{
        isImporting,
        progress,
        isMinimized,
        startImport,
        updateProgress,
        completeImport,
        minimizeModal,
        restoreModal,
        cancelImport,
        setOnImportComplete
      }}
    >
      {children}
    </CsvImportContext.Provider>
  );
};

export const useCsvImportContext = (): CsvImportContextState => {
  const context = useContext(CsvImportContext);
  if (!context) {
    throw new Error('useCsvImportContext must be used within a CsvImportProvider');
  }
  return context;
};
